import { useCallback, useEffect, useState, useRef } from 'react';
import { AppConnectionStatuses } from 'integrations-lib/constants/appConnectionStatuses';
import { AuthorizationStatusView } from './AuthorizationStatusView';
import { shouldHideAppStatuses } from '../../utils/appsWithHiddenStatuses';
import { useLazyGetAppStatuses } from '../../hooks/useLazyGetAppStatuses';
import { getMostRecentAccountsStatusesList } from '../../utils/authorizationStatuses';
import { jsx as _jsx } from "react/jsx-runtime";
const POLLING_INTERVAL = 500;
const UPDATE_TIMEOUT = 3000;
const AuthorizationStatusContent = ({
  appId,
  alertStore,
  showInstalledStatus,
  showTooltip = true,
  tracker
}) => {
  const [statusUpdating, setStatusUpdating] = useState(false);
  const prevStatusRef = useRef(undefined);
  const {
    data: statusesData,
    loading,
    error,
    fetchStatuses,
    startPolling,
    stopPolling
  } = useLazyGetAppStatuses({
    appId,
    categories: ['AUTHORIZATION']
  });

  /**
   * `accountStatusesList` contains the most recent status for each `account`.
   * `Account` is defined by the unique combination of fields like: `externalAccountId`, `userId`, `businessUnitId`.
   */
  const accountStatusesList = getMostRecentAccountsStatusesList(statusesData);

  /**
   * Is necessary for case of single account (update of status after reconnect)
   */
  const currentStatus = accountStatusesList.length === 1 ? accountStatusesList[0].type : prevStatusRef.current;
  const startUpdating = useCallback(() => {
    if (currentStatus !== AppConnectionStatuses.OK) {
      startPolling(POLLING_INTERVAL);
      prevStatusRef.current = currentStatus;
      setStatusUpdating(true);
    }
  }, [currentStatus, startPolling]);
  const stopUpdating = useCallback(() => {
    stopPolling();
    prevStatusRef.current = currentStatus;
    setStatusUpdating(false);
  }, [currentStatus, stopPolling]);
  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);
  useEffect(() => {
    if ((currentStatus === AppConnectionStatuses.OK || currentStatus !== prevStatusRef.current) && statusUpdating) {
      stopUpdating();
    }
  }, [statusUpdating, stopUpdating, currentStatus]);

  /**
   * For single account scenario (reconnect directly from the component)
   * Updating starts after the reconnection and stops after the status is changed or due to timeout
   */
  const onAuthSucceeded = useCallback(() => {
    startUpdating();
    const timeoutId = setTimeout(() => {
      if (statusUpdating) {
        stopUpdating();
      }
    }, UPDATE_TIMEOUT);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [statusUpdating, startUpdating, stopUpdating]);
  return /*#__PURE__*/_jsx(AuthorizationStatusView, {
    accountStatusesList: accountStatusesList || [],
    alertStore: alertStore,
    loading: loading || statusUpdating,
    error: error,
    onAuthSucceeded: onAuthSucceeded,
    showInstalledStatus: showInstalledStatus,
    showTooltip: showTooltip,
    tracker: tracker
  });
};
export const AuthorizationStatus = props => {
  if (shouldHideAppStatuses(props.appId)) {
    return null;
  }
  return /*#__PURE__*/_jsx(AuthorizationStatusContent, Object.assign({}, props));
};